/**
 * Роутинг WS `/client/event/form/{coreId}` → FormMessagesOverlay (Swing soft-events).
 *
 * Инициализация: `initFormEventRouter` из GlobalProvider.onMounted.
 */
import type { CoreNotifyEvent } from '@/services/websocket/types'
import { getWebSocketService } from '@/services/websocket/websocket.service'
import { useAppStore } from '@/stores/app'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'
import {
  mapSoftEventToMessage,
  readFormSoftParamIdentity,
  shouldAutoExecuteFormSoftReload,
  type FormMessageActionType,
  type FormSoftMessage,
} from '@/services/form-events/formSoftEvent'
import {
  addFormSoftMessage,
  clearFormSoftMessages,
  removeFormSoftMessage,
} from '@/services/form-events/formMessagesStore'
import { resolveFormSlotsForCoreEvent } from '@/services/form-events/resolveFormEventSlots'
import {
  isSlotActionInFlight,
  waitForSlotActionIdle,
  __resetSlotActionInFlightForTests,
} from '@/services/form-events/slotActionInFlight'

export type FormEventRouterDeps = {
  reloadForm: (ctx: {
    pageDataSlot: string
    formId?: string
    sessionId?: string
  }) => Promise<unknown> | unknown
  closeForm: (ctx: { pageDataSlot: string; sessionId?: string }) => Promise<unknown> | unknown
}

let routerDeps: FormEventRouterDeps | null = null
let formUnsubscribe: (() => void) | null = null

/** STACK already running for a slot (`submit=auto` / overlay reload_form). */
const reloadInFlightBySlot = new Map<string, Promise<void>>()
/** Latest reload request that arrived while the slot STACK was in flight. */
const trailingReloadBySlot = new Map<string, FormSoftMessage>()

function unwrapEventData(data: unknown): Record<string, unknown> | undefined {
  if (!data || typeof data !== 'object') return undefined
  const record = data as Record<string, unknown>
  // Already fields map
  if (record._event != null || record['_user.reverseShortName'] != null) {
    return record
  }
  const nested = (record as { fields?: { fields?: Record<string, unknown> } }).fields?.fields
  if (nested && typeof nested === 'object') return nested
  return record
}

export { resolveFormSlotsForCoreEvent }

export function routeFormSoftEvent(event: CoreNotifyEvent): FormSoftMessage[] {
  const coreId = String(event.coreId ?? '').trim()
  if (!coreId) return []

  const data = unwrapEventData(event.data)
  const payloadSessionId =
    (data?._sessionId && typeof data._sessionId === 'object' && data._sessionId !== null
      ? (data._sessionId as { value?: unknown }).value
      : data?._sessionId) ?? null

  const appStore = useAppStore()
  const pagesData = appStore.pagesData as Record<string, unknown>
  const dialogs = (appStore.dialogs || {}) as Record<string, unknown>
  const overlays = (appStore.overlays || {}) as Record<string, unknown>
  const activeSessionId =
    appStore.currentSessionId != null ? String(appStore.currentSessionId).trim() : ''
  const fallbackSlotId =
    appStore.currentPageId != null ? String(appStore.currentPageId).trim() : ''
  const slotIds = resolveFormSlotsForCoreEvent({
    coreId,
    payloadSessionId: payloadSessionId != null ? String(payloadSessionId) : null,
    pagesData,
    dialogs,
    overlays,
    fallbackSlotId: fallbackSlotId || null,
    activeSessionId: activeSessionId || null,
    paramIdentity: readFormSoftParamIdentity(data),
  })

  if (slotIds.length === 0) {
    const knownSessions = [
      ...Object.entries(pagesData).map(([id, e]) => ({
        slot: id,
        session: readPlatformSessionIdFromPageEntry(e),
      })),
      ...Object.entries(dialogs).map(([id, e]) => ({
        slot: id,
        session: readPlatformSessionIdFromPageEntry(e),
      })),
    ].filter((row) => row.session)
    console.warn('[formEventRouter] no form slot for soft event', {
      coreId,
      eventType: event.type,
      payloadSessionId,
      activeSessionId,
      fallbackSlotId,
      knownSessions,
    })
    return []
  }

  const created: FormSoftMessage[] = []
  for (const slotId of slotIds) {
    const pageEntry = pagesData[slotId] ?? dialogs[slotId] ?? overlays[slotId] ?? null
    const formId = pageEntry
      ? resolveElementRegistryFormId(pageEntry as Record<string, any>, slotId)
      : slotId
    const message = mapSoftEventToMessage({
      eventType: event.type,
      data,
      coreId,
      slotId,
      formId,
    })
    if (!message) continue

    const shouldAutoReload = shouldAutoExecuteFormSoftReload(message)
    const hasOnlyHideAction =
      message.actions.length === 0 ||
      (message.actions.length === 1 && message.actions[0] === 'hide_message')

    if (shouldAutoReload) {
      // Contract: `_param.submit=auto` → STACK, no balloon. Wait out SAME_WINDOW
      // doAction on this slot so we do not STACK a core that is already dying (4004).
      created.push(message)
      void scheduleSubmitAutoReload(message)
      continue
    }

    if (hasOnlyHideAction) {
      addFormSoftMessage({
        ...message,
        autoCountdownMs: 3000,
        autoCountdownAction: 'hide_message',
      })
      created.push(message)
      continue
    }

    addFormSoftMessage(message)
    created.push(message)
  }
  return created
}

function readSlotSessionId(slotId: string): string | null {
  const appStore = useAppStore()
  const pagesData = appStore.pagesData as Record<string, unknown>
  const dialogs = (appStore.dialogs || {}) as Record<string, unknown>
  const overlays = (appStore.overlays || {}) as Record<string, unknown>
  const entry = pagesData[slotId] ?? dialogs[slotId] ?? overlays[slotId] ?? null
  return readPlatformSessionIdFromPageEntry(entry)
}

function isCoreMountedOnAnySlot(coreId: string): boolean {
  const id = String(coreId ?? '').trim()
  if (!id) return false
  const appStore = useAppStore()
  const buckets = [
    appStore.pagesData as Record<string, unknown>,
    (appStore.dialogs || {}) as Record<string, unknown>,
    (appStore.overlays || {}) as Record<string, unknown>,
  ]
  return buckets.some((bucket) =>
    Object.values(bucket).some((entry) => readPlatformSessionIdFromPageEntry(entry) === id),
  )
}

/**
 * After the slot's own action replaced the core (2609→2610 SAME_WINDOW), the
 * `_changedState` is a side-effect — the new form is already applied. Skip STACK.
 * Idle slot, or an action that did not change session (dialog / live 2610) — reload.
 */
async function scheduleSubmitAutoReload(message: FormSoftMessage): Promise<void> {
  const slotId = String(message.slotId ?? '').trim()
  if (!slotId) return

  const sessionBefore = readSlotSessionId(slotId)
  const wasBusy = isSlotActionInFlight(slotId)
  await waitForSlotActionIdle(slotId)
  const sessionAfter = readSlotSessionId(slotId)

  if (wasBusy && sessionBefore && sessionAfter && sessionBefore !== sessionAfter) {
    return
  }

  const eventCore = String(message.coreId ?? '').trim()
  if (
    eventCore &&
    sessionAfter &&
    sessionAfter !== eventCore &&
    !isCoreMountedOnAnySlot(eventCore)
  ) {
    // Notify for a core this window already replaced (27616). Do not STACK the leftover.
    return
  }

  await executeFormSoftMessageAction(message, 'reload_form')
}

/**
 * One STACK at a time per slot. Extra reload_form while in-flight becomes a
 * single trailing run with the latest message (do not drop the last state).
 */
async function runReloadFormAction(message: FormSoftMessage): Promise<void> {
  const slotId = String(message.slotId ?? '').trim()
  if (!slotId) return

  const inFlight = reloadInFlightBySlot.get(slotId)
  if (inFlight) {
    trailingReloadBySlot.set(slotId, message)
    await inFlight
    return
  }

  const chain = (async () => {
    let next: FormSoftMessage | undefined = message
    while (next) {
      const current = next
      next = undefined
      const deps = routerDeps
      if (!deps) {
        console.warn('[formEventRouter] deps not initialized for action', 'reload_form')
        return
      }
      try {
        await deps.reloadForm({
          pageDataSlot: slotId,
          formId: current.formId,
          sessionId: current.coreId,
        })
        // Форма уже актуальная — стопка «внесены изменения» на этом слоте больше не нужна.
        clearFormSoftMessages(slotId)
      } catch (error) {
        console.error('[formEventRouter] reload_form failed', error)
      }
      next = trailingReloadBySlot.get(slotId)
      if (next) trailingReloadBySlot.delete(slotId)
    }
  })()

  reloadInFlightBySlot.set(slotId, chain)
  try {
    await chain
  } finally {
    if (reloadInFlightBySlot.get(slotId) === chain) {
      reloadInFlightBySlot.delete(slotId)
    }
    const leftover = trailingReloadBySlot.get(slotId)
    if (leftover && !reloadInFlightBySlot.has(slotId)) {
      trailingReloadBySlot.delete(slotId)
      void runReloadFormAction(leftover)
    }
  }
}

export async function executeFormSoftMessageAction(
  message: FormSoftMessage,
  action: FormMessageActionType,
): Promise<void> {
  const deps = routerDeps
  if (!deps) {
    console.warn('[formEventRouter] deps not initialized for action', action)
    return
  }

  if (action === 'hide_message') {
    removeFormSoftMessage(message.slotId, message.id)
    return
  }

  if (action === 'reload_form') {
    await runReloadFormAction(message)
    return
  }

  if (action === 'close_form') {
    try {
      await deps.closeForm({
        pageDataSlot: message.slotId,
        sessionId: message.coreId,
      })
    } catch (error) {
      console.error('[formEventRouter] close_form failed', error)
    } finally {
      removeFormSoftMessage(message.slotId, message.id)
    }
  }
}

function onInboundFormEvent(event: CoreNotifyEvent): void {
  try {
    routeFormSoftEvent(event)
  } catch (error) {
    console.error('[formEventRouter] failed to route form event', error)
  }
}

/** Подключить роутер (один раз, из GlobalProvider.onMounted). */
export function initFormEventRouter(deps: FormEventRouterDeps): () => void {
  routerDeps = deps
  if (!formUnsubscribe) {
    formUnsubscribe = getWebSocketService().onFormEvent(onInboundFormEvent)
  }
  return () => {
    formUnsubscribe?.()
    formUnsubscribe = null
    routerDeps = null
  }
}

/** @internal */
export function __resetFormEventRouterForTests(): void {
  formUnsubscribe?.()
  formUnsubscribe = null
  routerDeps = null
  reloadInFlightBySlot.clear()
  trailingReloadBySlot.clear()
  __resetSlotActionInFlightForTests()
}
